<template>
  <div class="flex flex-row justify-between py-16">
    <div class="flex flex-row items-center">
      <p class="heading-xs">{{ title }}</p>
    </div>
    <div class="flex flex-row items-center">
      <AppButton
        class="me-8"
        size="sm"
        :disabled="leftButtonIsDisabled"
        squared
        color="secondary"
        aria-label="scrolla vänster i produktslider"
        @click="scrollOneCardLeft"
      >
        <nuxt-icon name="arrow-left" aria-hidden="true" />
      </AppButton>
      <AppButton
        color="secondary"
        size="sm"
        :disabled="rightButtonIsDisabled"
        aria-label="scrolla höger i produktslider"
        squared
        @click="scrollOneCardRight"
      >
        <nuxt-icon name="arrow-right" aria-hidden="true" />
      </AppButton>
    </div>
  </div>

  <ul
    ref="scrollRef"
    class="cards-container flex flex-row overflow-x-auto scroll-smooth snap-x pb-16"
  >
    <li v-for="(card, index) in cards" :key="card.id ?? card.key ?? index">
      <div
        :class="{
          [cardWidthClass]: true,
          [cardGapClass]: index !== cards.length - 1,
          [cardGapClassLastCard]: index === cards.length - 1,
        }"
        class="h-full"
      >
        <slot :card="card" :index="index"></slot>
      </div>
    </li>
  </ul>
</template>

<script lang="ts" setup generic="T extends { id?: number; key?: string }">
import { useSsrSafeViewportState } from '~/composables/general/useSsrSafeViewportState';

const CARD_WIDTH_PX_MOBILE = 148;
const CARD_WIDTH_CLASS_MOBILE = 'w-[148px]';
const CARD_WIDTH_PX_MEDIUM = 246;
const CARD_WIDTH_CLASS_MEDIUM = 'w-[246px]';
const CARD_WIDTH_PX_LARGE = 256;
const CARD_WIDTH_CLASS_LARGE = 'w-[256px]';
const CARD_PADDING_PX = 16;
const SCROLL_ZERO_THRESHOLD = 0.9;

interface Props {
  cards: T[];
  title?: string;
}

defineProps<Props>();

const { screenIsSm, screenIsMd, screenLargerOrEqualsMd } =
  useSsrSafeViewportState();

const cardWidth: Ref<number> = ref(CARD_WIDTH_PX_MOBILE);
const cardWidthClass: Ref<string> = ref(CARD_WIDTH_CLASS_MOBILE);
const cardGapClass = 'me-[16px]';
const cardGapClassLastCard = 'me-96';
const scrollRef: Ref<HTMLDivElement | null> = ref(null);

const calcAmountToScrollInPixels = computed(
  () => cardWidth.value + CARD_PADDING_PX,
);

const scrollOneCardRight = () => {
  if (scrollRef.value) {
    scrollRef.value.scrollLeft += calcAmountToScrollInPixels.value;
  }
};

const scrollOneCardLeft = () => {
  if (scrollRef.value) {
    scrollRef.value.scrollLeft -= calcAmountToScrollInPixels.value;
  }
};

const leftButtonIsDisabled: Ref<boolean> = ref(false);
const rightButtonIsDisabled: Ref<boolean> = ref(false);

watch(
  [screenIsSm, screenIsMd, screenLargerOrEqualsMd],
  ([newScreenIsSm, newScreenIsMd, newScreenLargerOrEqualsMd]) => {
    if (newScreenIsSm) {
      cardWidth.value = CARD_WIDTH_PX_MOBILE;
      cardWidthClass.value = CARD_WIDTH_CLASS_MOBILE;
    } else if (newScreenIsMd) {
      cardWidth.value = CARD_WIDTH_PX_MEDIUM;
      cardWidthClass.value = CARD_WIDTH_CLASS_MEDIUM;
    } else if (newScreenLargerOrEqualsMd) {
      cardWidth.value = CARD_WIDTH_PX_LARGE;
      cardWidthClass.value = CARD_WIDTH_CLASS_LARGE;
    }
  },
  { immediate: true },
);

onMounted(() => {
  const disableLeftButtonIfScrollStopped = (scrollAmount: number) => {
    leftButtonIsDisabled.value = scrollAmount < SCROLL_ZERO_THRESHOLD;
  };

  const disableRightButtonIfScrollStopped = (
    scrollAmount: number,
    scrollMax: number,
  ) => {
    rightButtonIsDisabled.value =
      scrollMax - scrollAmount < SCROLL_ZERO_THRESHOLD;
  };

  const handleHorizontalScroll = (e: Event) => {
    if (e.target) {
      const scrollAmount = (e.target as any).scrollLeft as number;
      const scrollMax = (e.target as any).scrollLeftMax as number;

      disableLeftButtonIfScrollStopped(scrollAmount);
      disableRightButtonIfScrollStopped(scrollAmount, scrollMax);
    }
  };

  useEventListener(scrollRef, 'scroll', handleHorizontalScroll);

  if (scrollRef.value) {
    const initialScrollAmount = scrollRef.value.scrollLeft;
    disableLeftButtonIfScrollStopped(initialScrollAmount);
  }
});
</script>

<style scoped lang="scss">
.cards-container {
  scrollbar-width: none;
}
</style>
